/**
 * SceneSettingsPanel - Floating scene-options card (',' shortcut)
 *
 * Small dock-styled floating card with SCENE options wired live to the
 * SceneManager: ambient / sun / fill light intensities, shadow opacity
 * (the ShadowMaterial catchers) and background tone (light grey <-> white,
 * applied to the scene background AND as a tint on the dot-grid ground).
 *
 * Toggled with the ',' key (editor convention); Escape closes. Settings
 * persist in localStorage ('sketchchair-scene-settings') and are applied
 * at startup (panel construction).
 */

const STORAGE_KEY = 'sketchchair-scene-settings';

const DEFAULTS = {
    ambient: 1.85, // Greg's preferred bright look (2026-07-10)
    sun: 1.9,
    fill: 2,
    shadow: 0.26,
    tone: 1        // pure white background
};

const FIELDS = [
    { key: 'ambient', label: 'Ambient light', min: 0, max: 2, step: 0.05 },
    { key: 'sun', label: 'Sun light', min: 0, max: 2, step: 0.05 },
    { key: 'fill', label: 'Fill light', min: 0, max: 2, step: 0.05 },
    { key: 'shadow', label: 'Shadow opacity', min: 0, max: 1, step: 0.02 },
    { key: 'tone', label: 'Background tone', min: 0, max: 1, step: 0.01 }
];

export class SceneSettingsPanel {
    constructor(application) {
        this.app = application;
        this.settings = this._load();
        this.visible = false;
        this._createCard();
        // Apply persisted settings at startup
        this.apply();
        console.log("Scene settings: press ',' to open (Escape closes)");
    }

    _load() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
        } catch (_) { /* corrupted settings: fall back to defaults */ }
        return { ...DEFAULTS };
    }

    _save() {
        clearTimeout(this._saveTimer);
        this._saveTimer = setTimeout(() => {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
            } catch (_) { /* storage full/blocked */ }
        }, 250);
    }

    /** Push the current settings into the live scene */
    apply() {
        const s = this.settings;
        const sm = this.app.sceneManager;
        if (!sm) return;

        // Lights were pushed in setupLights order: ambient, sun, fill
        const [ambient, sun, fill] = sm.lights || [];
        if (ambient) ambient.intensity = s.ambient;
        if (sun) sun.intensity = s.sun;
        if (fill) fill.intensity = s.fill;

        // Shadow catchers: the dot-grid catcher (Application.groundVisuals)
        // and SceneManager's legacy ground plane, when present
        const shadowPlane = this.app.groundVisuals?.[1];
        if (shadowPlane?.material) shadowPlane.material.opacity = s.shadow;
        if (sm.groundPlane?.material) sm.groundPlane.material.opacity = s.shadow;

        // Background tone: lerp light grey -> white for the scene
        // background, and tint the dot-grid plane's baked texture to match
        const lerp = (a, b, t) => a + (b - a) * t;
        const bg = lerp(0.88, 1.0, s.tone);
        if (sm.scene?.background?.setRGB) sm.scene.background.setRGB(bg, bg, bg);
        const dotPlane = this.app.groundVisuals?.[0];
        if (dotPlane?.material?.color) {
            const tint = lerp(0.9, 1.0, s.tone);
            dotPlane.material.color.setRGB(tint, tint, tint);
        }
    }

    _createCard() {
        const card = document.createElement('div');
        card.id = 'scene-settings-card';
        card.style.cssText = `
            position: fixed; top: 76px; left: 50%; transform: translateX(-50%);
            width: 250px; z-index: 60; display: none;
            background: rgba(255, 255, 255, 0.96); backdrop-filter: blur(8px);
            border: 1px solid rgba(0, 0, 0, 0.08); border-radius: 12px;
            box-shadow: 0 8px 28px rgba(0, 0, 0, 0.14);
            padding: 12px 14px; font-size: 12px; color: #333;`;

        const header = document.createElement('div');
        header.style.cssText = `
            display: flex; align-items: center; justify-content: space-between;
            margin-bottom: 8px;`;
        const title = document.createElement('div');
        title.textContent = 'Scene settings';
        title.style.cssText = 'font-weight: 600; font-size: 12px; letter-spacing: 0.3px;';
        title.title = "Toggle with ','";
        header.appendChild(title);
        const close = document.createElement('button');
        close.textContent = '×';
        close.title = 'Close (Escape)';
        close.style.cssText = `
            border: none; background: transparent; font-size: 16px; cursor: pointer;
            color: #888; padding: 0 2px; line-height: 1;`;
        close.addEventListener('click', () => this.hide());
        header.appendChild(close);
        card.appendChild(header);

        this.rows = new Map();
        for (const f of FIELDS) {
            const row = document.createElement('div');
            row.style.cssText = 'display: flex; flex-direction: column; gap: 2px; margin-bottom: 8px;';

            const labelRow = document.createElement('div');
            labelRow.style.cssText = 'display: flex; justify-content: space-between;';
            const label = document.createElement('span');
            label.textContent = f.label;
            label.style.color = '#666';
            const value = document.createElement('span');
            value.style.color = '#999';
            labelRow.appendChild(label);
            labelRow.appendChild(value);
            row.appendChild(labelRow);

            const slider = document.createElement('input');
            slider.type = 'range';
            slider.min = String(f.min);
            slider.max = String(f.max);
            slider.step = String(f.step);
            slider.value = String(this.settings[f.key]);
            slider.style.cssText = 'width: 100%;';
            slider.addEventListener('input', () => {
                this.settings[f.key] = parseFloat(slider.value);
                value.textContent = slider.value;
                this.apply();
                this._save();
            });
            row.appendChild(slider);

            value.textContent = slider.value;
            this.rows.set(f.key, { slider, value });
            card.appendChild(row);
        }

        const hint = document.createElement('div');
        hint.textContent = "Shortcut: ','  ·  Escape closes";
        hint.style.cssText = 'color: #aaa; font-size: 10px; text-align: center; margin-top: 2px;';
        card.appendChild(hint);

        document.body.appendChild(card);
        this.card = card;
    }

    show() {
        // Refresh sliders from the persisted state
        for (const [key, row] of this.rows) {
            row.slider.value = String(this.settings[key]);
            row.value.textContent = row.slider.value;
        }
        this.card.style.display = 'block';
        this.visible = true;
    }

    hide() {
        this.card.style.display = 'none';
        this.visible = false;
    }

    toggle() {
        if (this.visible) this.hide();
        else this.show();
    }
}
